import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2, Phone, Users } from "lucide-react";
import { api } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const referralCode = searchParams.get("ref");

  useEffect(() => {
    // Validate referral code if present
    if (referralCode) {
      validateReferralCode();
    }
  }, [referralCode]);

  const validateReferralCode = async () => {
    try {
      const response = await api.get(`/referral/${referralCode}`);
      if (response.data.valid) {
        setReferralInfo(response.data);
      }
    } catch (error) {
      console.error("Error validating referral code:", error);
    }
  };

  // Determine branding to display
  const displayAppName = referralInfo?.app_name || "The Portfolio Accelerator";
  const displayLogoUrl = referralInfo?.logo_url ? `${API_URL}${referralInfo.logo_url}` : "/investeps-logo.png";
  const hasCustomBranding = referralInfo?.app_name || referralInfo?.logo_url;

  // Update document title and favicon for partner branding
  useEffect(() => {
    document.title = displayAppName;
    
    if (referralInfo?.logo_url) {
      const faviconLink = document.querySelector("link[rel='icon']");
      const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
      const fullLogoUrl = `${API_URL}${referralInfo.logo_url}`;
      
      if (faviconLink) {
        faviconLink.href = fullLogoUrl;
      }
      if (appleTouchIcon) {
        appleTouchIcon.href = fullLogoUrl;
      }
    }
  }, [displayAppName, referralInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mobile.trim()) {
      toast.error("Mobile number is required");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!acceptedPrivacy) {
      toast.error("Please accept the Privacy Policy to continue");
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, name, mobile, referralCode);
      
      // Track signup conversion with Meta Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'Portfolio Accelerator Signup',
          status: true
        });
      }
      
      toast.success("Account created successfully!");
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create account";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md">
        {/* Logo - Shows partner branding if available */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src={displayLogoUrl}
            alt={displayAppName} 
            className="w-12 h-12 rounded-xl object-contain"
          />
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">{displayAppName}</h1>
            <p className="text-sm text-slate-500">Property Portfolio Tracker</p>
          </div>
        </div>

        <Card className="shadow-lg border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl">Access Now</CardTitle>
            <CardDescription>Start tracking your property portfolio and plan your next purchase</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Referral Partner Badge - Only show if referred but hide if using custom branding */}
            {referralInfo && !hasCustomBranding && (
              <div className="flex items-center gap-2 p-3 bg-[#547d91]/10 rounded-lg border border-[#547d91]/20">
                <Users className="w-5 h-5 text-[#547d91]" />
                <div>
                  <p className="text-sm font-medium text-[#547d91]">
                    Referred by {referralInfo.partner_name}
                  </p>
                  {referralInfo.company && (
                    <p className="text-xs text-slate-500">{referralInfo.company}</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="0412 345 678"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className="pl-10 h-11"
                    required
                    data-testid="mobile-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-11"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              {/* Privacy Policy Checkbox */}
              <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Checkbox 
                  id="privacy-policy" 
                  checked={acceptedPrivacy}
                  onCheckedChange={(checked) => setAcceptedPrivacy(checked)}
                  className="mt-0.5"
                  data-testid="privacy-policy-checkbox"
                />
                <Label 
                  htmlFor="privacy-policy" 
                  className="text-sm text-slate-600 font-normal leading-relaxed cursor-pointer"
                >
                  I have read and agree to the{" "}
                  <Link 
                    to="/privacy-policy" 
                    className="text-[#547d91] font-medium hover:underline"
                    target="_blank"
                    data-testid="privacy-policy-link"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-red-500"> *</span>
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-[#547d91] hover:bg-[#456a7a]"
                disabled={isLoading || !acceptedPrivacy}
                data-testid="register-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Access Now"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="text-[#547d91] font-medium hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
            
            {/* Powered by Investeps */}
            <p className="text-center text-xs text-slate-400 mt-4">
              Powered by{" "}
              <a 
                href="https://www.investepsproperty.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#547d91] hover:underline"
              >
                Investeps Property
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
